import { useCallback, useMemo } from "react";
import { useBaseMutation } from "./useBaseMutation";
import { executeOptimisticApi } from "./optimisticApi";
import { includeGroup } from "../utils";
import {
  MutateFunction,
  UseBaseMutationResult,
  UseMutationOptions,
  UseMutationResult,
} from "./types";
import {
  Extensions,
  GraphqlErrorResponse,
  Variables,
  useGraphqlObjConfig,
} from "../graphql";
import { useGraphQLFetching, useQueryClient } from "../client";
import { QueryKey } from "../queryKey";
import { isQueryKey } from "../queryKey/types";
import { GqlObj } from "../graphql/types";

/**
 * Returns a mutate function to perform a graphql mutation to frontend-api.
 * After mutation, the queries will be refeched
 *
 * @remarks
 * You can customize the mutation with graphql operation directives:
 * - \@silent: Avoid perform queries after mutation
 * - \@update_groups(groups: "groupName1,groupName2"): Perform query after mutations only if mutation and query have a match in update groups
 *
 * @param query - Graphql mutation operation, builded with gql constructor
 *
 * @returns Mutate function, Mutation response and current status of the mutation
 */
export function useMutation<
  TData,
  TVariables extends Variables | undefined = undefined
>(
  query: GqlObj,
  {
    optimisticUpdate,
    updateOnSuccess,
    onError = () => {},
    onSuccess = () => {},
    emitOnError = false,
  }: UseMutationOptions<TData, TVariables> = {}
): UseMutationResult<TData, TVariables> {
  const emitBroadcast = useBroadcast(query);
  const { endpoint, schema } = useGraphqlObjConfig(query);
  const { fetcher, extensions, fetchTime } = useGraphQLFetching<
    TData,
    TVariables
  >();
  const queryClient = useQueryClient();
  const {
    mutate: baseMutate,
    data,
    error,
    status,
  } = useBaseMutation<
    TData,
    GraphqlErrorResponse<TData>,
    { query: GqlObj; variables: TVariables },
    { queryKey: QueryKey; currentQueryData: unknown }
  >((data) => fetcher(data, endpoint), {
    onSuccess(data, { variables }) {
      if (!query.directives.silent) {
        emitBroadcast();
      }
      if (updateOnSuccess) {
        const { queryKey, queryResolver } = updateOnSuccess({
          data,
          variables,
        });
        const currentData = queryClient.getQueryData(queryKey);
        const [, { query, variables: queryVars }] = queryKey;

        executeOptimisticApi({
          currentData,
          query: query.doc,
          resolvers: queryResolver,
          variables: queryVars,
          schema,
        })
          .then((result) => {
            queryClient.setQueryData(queryKey, result.data);
          })
          .catch(() => {
            // empty
          });
      }
      onSuccess({ data, variables });
    },
    onMutate({ variables: mutationVars }) {
      if (optimisticUpdate) {
        const { queryKey, queryResolver } = optimisticUpdate(mutationVars);
        const currentData = queryClient.getQueryData(queryKey);
        const [, { query, variables }] = queryKey;

        executeOptimisticApi({
          currentData,
          query: query.doc,
          resolvers: queryResolver,
          variables,
          schema,
        })
          .then((result) => {
            queryClient.setQueryData(queryKey, result.data);
          })
          .catch(() => {
            // empty
          });

        return { queryKey, currentQueryData: currentData };
      }
    },
    onError: ({ errors, status, data, headers }, { variables }, context) => {
      if (context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.currentQueryData);
      }

      if (emitOnError && !query.directives.silent) {
        emitBroadcast();
      }

      onError({
        errors,
        variables,
        headers,
        dataWithErrors: data,
        httpStatusCode: status,
      });
    },
  });
  const rest = { data, error, status };

  const mutate = useCallback<MutateFunction<TData, TVariables>>(
    (variables, { onSuccess = () => {}, onError = () => {} } = {}) => {
      baseMutate(
        { query, variables },
        {
          onSuccess: (data, { variables }) => onSuccess({ data, variables }),
          onError: ({ errors, status, data, headers }, { variables }) => {
            onError({
              errors,
              variables,
              headers,
              dataWithErrors: data,
              httpStatusCode: status,
            });
          },
        }
      );
    },
    [query, baseMutate]
  );
  return useMutationResults(
    mutate,
    rest,
    query.operationName,
    extensions,
    fetchTime
  );
}

const useMutationResults = <
  TData,
  TVariables extends Variables | undefined = undefined
>(
  mutate: MutateFunction<TData, TVariables>,
  {
    data,
    error,
    status,
  }: Pick<
    UseBaseMutationResult<
      TData,
      GraphqlErrorResponse<TData>,
      { query: GqlObj; variables: TVariables }
    >,
    "data" | "error" | "status"
  >,
  operationName: string,
  extensions: Extensions | null,
  fetchTime: number | null
): UseMutationResult<TData, TVariables> => {
  const {
    errors,
    data: dataWithErrors,
    status: httpStatusCode,
    headers,
  } = error || {};
  return useMemo<UseMutationResult<TData, TVariables>>(
    () => ({
      mutate,
      data: data || null,
      status,
      errors: errors || null,
      dataWithErrors,
      operationName,
      httpStatusCode,
      headers,
      extensions,
      fetchTime,
      loading: status === "loading",
    }),
    [
      mutate,
      data,
      status,
      errors,
      operationName,
      dataWithErrors,
      httpStatusCode,
      headers,
      extensions,
      fetchTime,
    ]
  );
};

const useBroadcast = (mutationObj: GqlObj): (() => void) => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries(
      {
        predicate(query) {
          if (!isQueryKey(query.queryKey)) {
            return false;
          }

          const [, queryArgs] = query.queryKey;
          return (
            query.isActive() &&
            includeGroup(mutationObj, queryArgs.query) &&
            !queryArgs.query.directives.ignoreUpdate
          );
        },
      },
      { cancelRefetch: true }
    );
  }, [queryClient, mutationObj]);
};
